/**
 * Transaction read helpers to manage seen/unread states for marketplace transactions and messages.
 */

const STORAGE_KEY_PREFIX = 'usedemoto_read_tx_';

/**
 * Get latest activity timestamp for a transaction (lastMessageAt, lastTransitionedAt, or createdAt).
 *
 * @param {Object} tx Transaction entity
 * @returns {number} Epoch timestamp in ms
 */
export const getTransactionLatestActivityTimestamp = tx => {
  if (!tx || !tx.attributes) {
    return 0;
  }
  const { lastMessageAt, lastTransitionedAt, createdAt } = tx.attributes;
  const dateVal = lastMessageAt || lastTransitionedAt || createdAt;
  if (!dateVal) {
    return 0;
  }
  const parsed = dateVal instanceof Date ? dateVal.getTime() : new Date(dateVal).getTime();
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Get stored read timestamps for current user.
 *
 * @param {string} userId User UUID
 * @returns {Object} Map of txId -> timestamp
 */
export const getStoredReadState = userId => {
  if (!userId || typeof window === 'undefined') {
    return {};
  }
  try {
    const raw = window.localStorage?.getItem(`${STORAGE_KEY_PREFIX}${userId}`);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

/**
 * Mark a transaction as read up to a certain timestamp (default: Date.now()).
 *
 * @param {string} userId User UUID
 * @param {string} txId Transaction UUID
 * @param {number} [timestamp] Optional timestamp, defaults to Date.now()
 */
export const markTransactionAsRead = (userId, txId, timestamp = Date.now()) => {
  if (!userId || !txId || typeof window === 'undefined') {
    return;
  }
  try {
    const currentMap = getStoredReadState(userId);
    const updatedMap = {
      ...currentMap,
      [txId]: timestamp,
    };
    window.localStorage?.setItem(`${STORAGE_KEY_PREFIX}${userId}`, JSON.stringify(updatedMap));
  } catch (e) {
    // Ignore localStorage errors
  }
};

/**
 * Check whether a transaction is unread for a given user.
 *
 * @param {string} userId User UUID
 * @param {Object} tx Transaction entity
 * @returns {boolean} True if transaction has unread activity
 */
export const isTransactionUnread = (userId, tx) => {
  if (!userId || !tx) {
    return false;
  }
  const latestActivity = getTransactionLatestActivityTimestamp(tx);
  if (!latestActivity) {
    return false;
  }
  const txUuid = tx.id?.uuid || tx.id;
  const readMap = getStoredReadState(userId);
  const lastRead = readMap[txUuid] || 0;
  return latestActivity > lastRead;
};
